import * as z from "zod";
import type { ApiContext } from "../context";
import {
  type ApiNotAuthorized,
  CapabilityRequired,
  ErrorCode,
  OfferNotForSale,
  StoreNotFound,
  UnexpectedApiError,
  ValidationError,
} from "../errors";
import { parse } from "../model/common";
import { PublicOffer } from "../model/public-offer";
import { forward, type Result } from "../result";
import type { Transport } from "../transport";

const PublicOffersResponse = z.object({ offers: z.array(PublicOffer) });

/**
 * A shop's catalogue with no session: the sealed products it sells to anyone.
 * Pass `anonymous`. Carting one is `CustomerCartApi.create`/`addItems` with
 * `offers`, behind the guest session.
 */
export class PublicOffersApi {
  constructor(private readonly t: Transport) {}

  /** What a shop or counter (`loc_<id>`) has on sale, in stock only. `CapabilityRequired`: the shop sells nothing at the counter now. */
  async list(
    ctx: ApiContext,
    shopId: string,
  ): Promise<Result<PublicOffer[], ApiNotAuthorized | StoreNotFound | CapabilityRequired>> {
    const r = await this.t.get(ctx, `/public/shops/${shopId}/offers`);
    return forward(
      r,
      (j) => parse(PublicOffersResponse, j).offers,
      (e) => {
        if (e instanceof ValidationError && e.code === ErrorCode.CapabilityRequired) {
          return new CapabilityRequired(e.incorrectValue, e.detailedDesc, e);
        }
        if (e instanceof ValidationError && e.code !== ErrorCode.RateLimited) return new StoreNotFound(shopId, e);
        if (e instanceof ValidationError) throw new UnexpectedApiError(e);
        return e;
      },
    );
  }

  /** One offer, sold out or not. */
  async get(ctx: ApiContext, offerId: string): Promise<Result<PublicOffer, ApiNotAuthorized | OfferNotForSale>> {
    const r = await this.t.get(ctx, `/public/offers/${offerId}`);
    return forward(
      r,
      (j) => parse(PublicOffer, j),
      (e) => {
        if (e instanceof ValidationError && e.code !== ErrorCode.RateLimited) {
          return new OfferNotForSale(e.detailedDesc, e);
        }
        if (e instanceof ValidationError) throw new UnexpectedApiError(e);
        return e;
      },
    );
  }
}
